import * as THREE from 'three';
import type { SkyPhase, Weather, WeatherKind } from '../../shared/types.js';
import type { Grade } from './post.js';

/**
 * Turning the conditions at a real place into a scene.
 *
 * Split deliberately in two. `lookFor` is a pure function from a `Weather` to a set of
 * colours, intensities and a light direction — no three.js state, no GL context, testable
 * in a unit test — and everything below it just applies that answer. The reason is that
 * nearly every judgement worth arguing about lives in the palette, and a palette you can
 * only inspect by taking a screenshot is a palette nobody will ever check.
 *
 * The governing constraint is not realism. A city at three in the morning under heavy cloud
 * is, truthfully, almost black, and a racing game that renders that truthfully is a racing
 * game nobody can play. So the night palette lifts the floor and warms the bounce light to
 * the colour of street lighting: a real city at night is orange, not black, and leaning on
 * that is how this stays both dark and drivable.
 */

export interface WeatherLook {
  skyTop: number;
  skyHorizon: number;
  /**
   * The haze: the sky right at the horizon, which is also what distant ground fades into —
   * one colour, so the two cannot be told apart. See `atmosphere.ts`.
   */
  fog: number;
  fogNear: number;
  fogFar: number;
  keyColor: number;
  keyIntensity: number;
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  /**
   * How much the sky itself lights what can see it, through the environment map — the soft
   * light that comes from the whole dome rather than from one direction, and what glass
   * and a wet road reflect.
   */
  envIntensity: number;
  /** The halo round the sun in the sky and in the haze: its colour and brightness. */
  glow: number;
  glowStrength: number;
  /** Exposure, white balance and contrast, applied before the tone curve. */
  grade: Grade;
  /** Unit vector pointing *at* the key light, in world space. */
  keyDirection: THREE.Vector3;
  /**
   * Whether a shadow pass is worth running at all. Under a heavy overcast or at night
   * there is no shadow to cast, and the pass is the most expensive thing in the frame.
   */
  shadows: boolean;
  /** Rain, snow or nothing, and how hard. */
  precipitation: { kind: 'rain' | 'snow'; intensity: number } | null;
  lightning: boolean;
  /**
   * How wet the streets are, 0–1: damp from a drizzle, standing water in a downpour. Rain and
   * storms only — snow lies on a street rather than wetting it, and nothing here draws that.
   */
  wet: number;
  /**
   * How far the city's own lights have come on: 0 by day, 1 once it is properly dark.
   *
   * Continuous rather than stepped off `phase`, because this is the one thing on screen
   * that would be caught changing. Windows and street lamps fading up over the twenty
   * minutes either side of sunset is what evening looks like; every light in Kraków coming
   * on at once, the instant the sun crosses a threshold, is what a bug looks like.
   */
  night: number;
}

// --- palettes ---------------------------------------------------------------

interface Palette {
  skyTop: number;
  skyHorizon: number;
  fog: number;
  fogNear: number;
  fogFar: number;
  keyColor: number;
  keyIntensity: number;
  hemiSky: number;
  hemiGround: number;
  hemiIntensity: number;
  envIntensity: number;
  glow: number;
  glowStrength: number;
  grade: Grade;
}

/**
 * Lit for a tone curve, not for a clipped screen (DECISIONS.md §80).
 *
 * The sun carries most of the light and the sky the rest, about four to one on a wall
 * turned towards it — what makes a sunlit pavement read as sunlit beside the shaded one. The
 * first palettes had the two nearly equal, because with nothing to roll the highlights off a
 * stronger sun simply clipped every pale wall to white; and with both equal every surface
 * landed within a few shades of every other.
 *
 * The bounce off the ground is warm masonry, not a neutral grey: most of what a street's
 * walls see below the horizon is the street and the building opposite.
 */
const PALETTES: Record<SkyPhase, Palette> = {
  day: {
    skyTop: 0x5f95cf,
    skyHorizon: 0xbcd0e0,
    fog: 0xc9d6e0,
    fogNear: 320,
    fogFar: 1500,
    keyColor: 0xfff1de,
    keyIntensity: 6.0,
    hemiSky: 0xdfe7ef,
    hemiGround: 0xa08a70,
    hemiIntensity: 1.2,
    envIntensity: 0.45,
    glow: 0xfff0dc,
    glowStrength: 1.1,
    grade: {
      exposure: 1,
      balance: [1.01, 1, 0.99],
      contrast: 1.1,
      saturation: 1.07,
      vignette: 0.12,
      bloomStrength: 0.2,
      bloomThreshold: 1.3,
      tiltShift: 0,
    },
  },
  golden: {
    skyTop: 0x4a80c0,
    skyHorizon: 0xf2bd88,
    fog: 0xe6c3a2,
    fogNear: 260,
    fogFar: 1300,
    keyColor: 0xffc48a,
    keyIntensity: 5.4,
    // Blue, against an orange sun: at golden hour the shadows are lit by a sky that is still
    // blue overhead, and the two colours side by side are the whole look.
    hemiSky: 0xaebfd6,
    hemiGround: 0xc88a62,
    hemiIntensity: 1.1,
    envIntensity: 0.45,
    glow: 0xffb06a,
    glowStrength: 2.2,
    grade: {
      exposure: 1.04,
      balance: [1.03, 1, 0.97],
      contrast: 1.12,
      saturation: 1.08,
      vignette: 0.14,
      bloomStrength: 0.24,
      bloomThreshold: 1.15,
      tiltShift: 0,
    },
  },
  twilight: {
    skyTop: 0x22305a,
    skyHorizon: 0x9a7389,
    fog: 0x67637f,
    fogNear: 180,
    fogFar: 1000,
    keyColor: 0xffab72,
    keyIntensity: 1.3,
    hemiSky: 0x4e5b8a,
    hemiGround: 0x4a3a34,
    hemiIntensity: 1.2,
    envIntensity: 0.45,
    glow: 0xff9a6a,
    glowStrength: 0.9,
    grade: {
      exposure: 1.08,
      balance: [1.025, 0.995, 1.015],
      contrast: 1.1,
      saturation: 1.08,
      vignette: 0.15,
      bloomStrength: 0.28,
      bloomThreshold: 1.05,
      tiltShift: 0,
    },
  },
  night: {
    // Lifted off the first version, which was measured at 18% of the daytime frame
    // brightness and was — in the words of the person who had to drive it — "very dark lol".
    // The floor is set by a single question: can you see the far kerb of the street you are
    // about to take at 100 km/h? At the original values, no.
    skyTop: 0x0a1124,
    // Sky glow: the haze over a city at night is lit from underneath by the city, and it is
    // what a street seen from the air at midnight dissolves into — warm, not navy.
    skyHorizon: 0x2c3150,
    fog: 0x3a3646,
    fogNear: 140,
    fogFar: 900,
    // Moonlight, not sunlight. Cool and weak, and mostly there so that flat surfaces at
    // different angles still read as different surfaces.
    keyColor: 0xa8bdec,
    // Strong enough to put a silver edge on every roof from the air, which is all that
    // makes a city legible from above at night once the street lights are under the eaves.
    keyIntensity: 1.1,
    // Sky cool, ground warm: the second of those is the sodium bounce that makes a night
    // city legible. Without it the roads and the buildings are the same shade of nothing.
    hemiSky: 0x2a3960,
    hemiGround: 0x8a5f2e,
    // 1.1 until the asphalt went darker (§83): between two lamps' pools the road then had
    // nothing left to show but the headlights.
    hemiIntensity: 1.35,
    envIntensity: 0.25,
    glow: 0x000000,
    glowStrength: 0,
    grade: {
      exposure: 1,
      balance: [0.985, 1, 1.035],
      contrast: 1.08,
      saturation: 1.06,
      vignette: 0.16,
      bloomStrength: 0.32,
      bloomThreshold: 1.0,
      tiltShift: 0,
    },
  },
};

/** Grey everything converges towards as the cloud thickens. */
const OVERCAST_GREY = 0x9aa4ae;
/**
 * At night the cloud is not grey: it is lit from underneath by the town, and turns the dull
 * orange-brown of sodium light bounced off a low ceiling. It was a dark slate once, and an
 * overcast night — which is most autumn nights in Poland — came out at a third of the
 * brightness of a clear one; "Leszno is too dark" was a cloudy evening. The real thing is
 * the other way round: a town under cloud is lighter than under stars, because the cloud
 * sends the street lighting back down.
 */
const OVERCAST_GLOW_NIGHT = 0x4e3c34;

export function lookFor(weather: Weather | null): WeatherLook {
  if (!weather) {
    const base = PALETTES.day;
    return {
      ...base,
      keyDirection: DEFAULT_SUN.clone(),
      shadows: true,
      precipitation: null,
      lightning: false,
      wet: 0,
      night: 0,
    };
  }

  const base = PALETTES[weather.phase];
  const night = weather.phase === 'night';
  // Cloud does two things at once and they pull in opposite directions: it kills the key
  // light and it *raises* the ambient, because an overcast sky is one enormous soft box.
  // Modelling only the first gives a flat grey scene with the contrast of a cave.
  const cover = clamp01(weather.cloud);
  const grey = night ? OVERCAST_GLOW_NIGHT : OVERCAST_GREY;

  const kind = weather.kind;
  const wet = kind === 'rain' || kind === 'storm';
  const murk = kind === 'fog' ? 1 : wet ? 0.55 * weather.precipitation + 0.25 : 0;

  const look: WeatherLook = {
    skyTop: mixHex(base.skyTop, grey, cover * 0.8),
    skyHorizon: mixHex(base.skyHorizon, grey, cover * 0.7),
    fog: mixHex(base.fog, kind === 'snow' ? mixHex(grey, 0xffffff, 0.45) : grey, cover * 0.7),
    // Visibility closes in with the weather rather than with the cloud: an overcast day is
    // dull, not short-sighted, and treating them the same made a grey afternoon in Poznań
    // look like a fog bank.
    fogNear: Math.max(28, base.fogNear * (1 - murk * 0.85)),
    fogFar: Math.max(200, base.fogFar * (1 - murk * 0.72)),
    keyColor: base.keyColor,
    keyIntensity: base.keyIntensity * (1 - cover * 0.78) * (kind === 'storm' ? 0.55 : 1),
    hemiSky: mixHex(base.hemiSky, grey, cover * 0.6),
    hemiGround: base.hemiGround,
    // At night the cloud has to give back what it took from the moon, which was most of the
    // light there was: the glow it holds is the fill for every street the lamps miss.
    hemiIntensity: base.hemiIntensity * (1 + cover * (night ? 0.55 : 0.28)),
    // The overcast sky is the light source now, so what it adds goes up as the sun's goes
    // down — but less than the sun lost, or a grey day would be as bright as a clear one.
    envIntensity: base.envIntensity * (1 + cover * 0.5),
    glow: base.glow,
    // The halo is the sun shining through clear air; cloud takes it away before it takes
    // the sun's shadows, which is why an overcast noon has no bright patch of sky at all.
    glowStrength: base.glowStrength * (1 - cover) ** 2,
    grade: {
      ...base.grade,
      // Under a lid of cloud the whole frame is darker, not just less contrasty — which is
      // what makes rain look like rain before a single drop is drawn.
      // Not at night, where the cloud is lit from below and the frame is already as dark as
      // it can be and still be driven.
      exposure: base.grade.exposure * (1 - cover * (night ? 0 : 0.18)),
      // A flat sky is a flat picture. Grading an overcast frame as hard as a sunny one
      // pulls its few shades apart into noise, so the push eases off with the light.
      contrast: 1 + (base.grade.contrast - 1) * (1 - cover * 0.6),
      saturation: 1 + (base.grade.saturation - 1) * (1 - cover * 0.8),
    },
    keyDirection: keyDirectionFor(weather),
    // Below about 8° the sun casts shadows longer than the shadow camera covers, and what
    // reaches the screen is a stretched smear across the whole street rather than the shape
    // of a building. Under thick cloud there is nothing to cast at all.
    shadows: weather.sunAltitude > 8 && cover < 0.8,
    precipitation: precipitationFor(kind, weather.precipitation),
    lightning: kind === 'storm',
    wet: wet ? Math.min(1, 0.35 + 0.65 * clamp01(weather.precipitation)) : 0,
    night: nightFor(weather),
  };
  return look;
}

/**
 * How far the lights have come on.
 *
 * Ramped between +6° and −6° of solar elevation, which brackets sunset: the first windows
 * light while the sun is still just up, and by the end of civil twilight the city is fully
 * lit. Heavy cloud brings it forward a little, which is both true and useful — an overcast
 * dusk is darker than a clear one, and it is the case where the street would otherwise be
 * hardest to see.
 */
function nightFor(weather: Weather): number {
  // Cloud raises the elevation at which the ramp starts, so the lights come on *earlier*.
  const dusk = 6 + clamp01(weather.cloud) * 3;
  return clamp01((dusk - weather.sunAltitude) / 12);
}

/**
 * Where the key light comes from.
 *
 * Above the horizon this is the sun, exactly: +X is east, +Z is *south* (see
 * `Projection`), so a bearing measured clockwise from north lands on
 * `(sin az, ·, −cos az)`.
 *
 * Below the horizon it is not the moon — the moon's real position is a different
 * calculation and, more to the point, half the time it is not up either. It is a stand-in
 * key placed high and behind the sun's bearing, whose only job is to keep surfaces at
 * different angles distinguishable. Sunlight is what a night scene must not look like, and
 * a weak cool light from an implausible angle reads as moonlight to everybody.
 */
function keyDirectionFor(weather: Weather): THREE.Vector3 {
  const azimuth = THREE.MathUtils.degToRad(weather.sunAzimuth);
  const altitude = THREE.MathUtils.degToRad(Math.max(weather.sunAltitude, 0));
  if (weather.sunAltitude <= 0) {
    const opposite = azimuth + Math.PI;
    // High in the sky, so it silvers the roofs — which is what keeps the city legible from
    // the air at midnight — and only grazes the walls, which at 52° it floodlit like a
    // searchlight (§84). The street lamps light the walls.
    const high = THREE.MathUtils.degToRad(72);
    return new THREE.Vector3(
      Math.cos(high) * Math.sin(opposite),
      Math.sin(high),
      -Math.cos(high) * Math.cos(opposite),
    ).normalize();
  }
  return new THREE.Vector3(
    Math.cos(altitude) * Math.sin(azimuth),
    Math.sin(altitude),
    -Math.cos(altitude) * Math.cos(azimuth),
  ).normalize();
}

function precipitationFor(
  kind: WeatherKind,
  intensity: number,
): WeatherLook['precipitation'] {
  if (kind === 'snow') return { kind: 'snow', intensity: clamp01(intensity) };
  if (kind === 'rain' || kind === 'storm') return { kind: 'rain', intensity: clamp01(intensity) };
  return null;
}

/** The sun as it was before there was any weather — the direction every screenshot is taken in. */
const DEFAULT_SUN = new THREE.Vector3(-260, 420, 180).normalize();

// ---------------------------------------------------------------------------
// Precipitation
// ---------------------------------------------------------------------------

/**
 * Rain and snow as one box of particles that rides with the camera.
 *
 * Falling is done in the vertex shader against a time uniform, with the height wrapped
 * modulo the box, so the CPU cost per frame is one uniform write no matter how many drops
 * there are. The alternative — rewriting a position buffer every frame — is a megabyte of
 * traffic to animate something the player is looking straight through.
 *
 * Rain is drawn as line segments rather than points because a raindrop at 8 m/s past a
 * moving camera is a streak, and a round dot at that speed reads unmistakably as snow. The
 * two endpoints of each segment share a base position and differ only by `along`, so the
 * streak's length and lean are shader constants and the wind can blow it sideways.
 */
const BOX = 90;
const RAIN_COUNT = 2600;
const SNOW_COUNT = 1500;
/** Metres a second. Terminal velocity for a decent-sized drop is about 9. */
const RAIN_SPEED = 26;
const SNOW_SPEED = 1.6;

export interface Precipitation {
  object: THREE.Object3D;
  update(camera: THREE.Camera, dt: number): void;
  dispose(): void;
}

export function createPrecipitation(kind: 'rain' | 'snow', intensity: number): Precipitation {
  const rain = kind === 'rain';
  const max = rain ? RAIN_COUNT : SNOW_COUNT;
  // A drizzle is not a downpour with the same number of drops drawn fainter. Scale the
  // count, and keep a floor so "light rain" is still visibly rain.
  const count = Math.max(120, Math.round(max * (0.25 + 0.75 * clamp01(intensity))));
  const vertices = rain ? count * 2 : count;

  const base = new Float32Array(vertices * 3);
  const along = new Float32Array(vertices);
  const speed = new Float32Array(vertices);
  const sway = new Float32Array(vertices);

  for (let i = 0; i < count; i++) {
    const x = (Math.random() - 0.5) * BOX;
    const y = Math.random() * BOX;
    const z = (Math.random() - 0.5) * BOX;
    // Individual drops fall at slightly different rates. Without the spread the whole box
    // moves as one sheet, which the eye picks up immediately as a texture scrolling.
    const v = (rain ? RAIN_SPEED : SNOW_SPEED) * (0.8 + Math.random() * 0.4);
    const phase = Math.random() * Math.PI * 2;
    const slots = rain ? 2 : 1;
    for (let k = 0; k < slots; k++) {
      const v3 = (i * slots + k) * 3;
      base[v3] = x;
      base[v3 + 1] = y;
      base[v3 + 2] = z;
      along[i * slots + k] = k;
      speed[i * slots + k] = v;
      sway[i * slots + k] = phase;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(base, 3));
  geometry.setAttribute('along', new THREE.BufferAttribute(along, 1));
  geometry.setAttribute('fall', new THREE.BufferAttribute(speed, 1));
  geometry.setAttribute('sway', new THREE.BufferAttribute(sway, 1));
  // The box travels with the camera and wraps, so no vertex is ever where its buffer says
  // it is. Culling it against that box would blink the whole field of rain in and out.
  geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), BOX);

  const uniforms = {
    uTime: { value: 0 },
    uOrigin: { value: new THREE.Vector3() },
    uBox: { value: BOX },
    uStreak: { value: rain ? 1.9 + 2.6 * clamp01(intensity) : 0 },
    uSize: { value: rain ? 1 : 2.4 + 1.6 * clamp01(intensity) },
    uOpacity: { value: rain ? 0.18 + 0.3 * clamp01(intensity) : 0.5 + 0.35 * clamp01(intensity) },
    uColor: { value: new THREE.Color(rain ? 0xc8d8e8 : 0xffffff) },
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    transparent: true,
    depthWrite: false,
    // Rain in front of a bright sky must darken nothing; additive blending is wrong for
    // both, but normal blending with a low alpha is right for both.
    blending: THREE.NormalBlending,
    fog: false,
    vertexShader: /* glsl */ `
      attribute float along;
      attribute float fall;
      attribute float sway;
      uniform float uTime;
      uniform vec3 uOrigin;
      uniform float uBox;
      uniform float uStreak;
      uniform float uSize;
      varying float vFade;
      void main() {
        vec3 p = position;
        // Wrap the fall inside the box, then re-centre it on the camera. Both are done in
        // world space so the field does not shear when the camera rotates.
        p.y = mod(p.y - uTime * fall, uBox);
        p.x += sin(uTime * 0.6 + sway) * (uStreak > 0.0 ? 0.6 : 2.2);
        p.z += cos(uTime * 0.45 + sway) * (uStreak > 0.0 ? 0.4 : 1.8);
        vec3 world = uOrigin + vec3(p.x, p.y - uBox * 0.5, p.z);
        world.y += along * uStreak;
        // Fade out at the top of the box so drops do not pop into existence overhead.
        vFade = 1.0 - smoothstep(uBox * 0.30, uBox * 0.5, p.y - uBox * 0.5);
        vec4 mv = viewMatrix * vec4(world, 1.0);
        gl_Position = projectionMatrix * mv;
        gl_PointSize = uSize * (60.0 / max(1.0, -mv.z));
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uSize;
      varying float vFade;
      void main() {
        float a = uOpacity * vFade;
        if (uSize > 1.5) {
          // Snow: round off the point sprite, or it is a blizzard of little squares.
          vec2 d = gl_PointCoord - 0.5;
          a *= smoothstep(0.5, 0.12, length(d));
        }
        if (a < 0.01) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });

  const object = rain
    ? new THREE.LineSegments(geometry, material)
    : new THREE.Points(geometry, material);
  object.frustumCulled = false;
  object.renderOrder = 900;
  object.name = `precipitation-${kind}`;

  let time = 0;
  return {
    object,
    update(camera, dt) {
      time += dt;
      uniforms.uTime.value = time;
      // Snapped to the box so the field does not visibly slide relative to the world as the
      // car drives; the wrap only works if the origin moves in whole box-widths.
      const at = camera.position;
      uniforms.uOrigin.value.set(at.x, Math.floor(at.y / BOX) * BOX + BOX * 0.5, at.z);
    },
    dispose() {
      geometry.dispose();
      material.dispose();
    },
  };
}

// ---------------------------------------------------------------------------

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

const _a = new THREE.Color();
const _b = new THREE.Color();

/**
 * `setHex` converts into the renderer's linear working space and `getHex` converts back,
 * so the interpolation happens in linear light — which is where a half-cloudy sky should be
 * mixed anyway. Mixing the sRGB bytes directly darkens every midpoint.
 */
export function mixHex(from: number, to: number, k: number): number {
  if (k <= 0) return from;
  if (k >= 1) return to;
  _a.setHex(from);
  _b.setHex(to);
  return _a.lerp(_b, k).getHex();
}
